import { useEffect } from "react"
import { Provider } from "@hydrologiq/shared/chakra/Provider"
import createStylingSystem from "@hydrologiq/shared/chakra/styling/styling"
// import { ApolloProvider } from "@apollo/client"
import { Provider as ReduxProvider } from "react-redux"
import AuthApolloProvider from "./graphql/Provider"
import { AuthProvider, useAuth } from "./auth/AuthProvider"
import { store } from "./state/store"
import { Spinner, Center } from "@chakra-ui/react"
import { graphqlUri } from "./config"
import { useVersionCheck, refreshApp } from "./hooks/useVersionCheck"
import { VersionToast } from "./components/version/VersionToast"
import { useDispatch, useSelector } from "react-redux"
import { dismissUpdate } from "./state/versionSlice"
import { RootState } from "./state/store"
import { prefetchAllSections } from "./sections"

const FullPageLoader = () => (
  <Center minH="100vh">
    <Spinner size="xl" color="blue.500" />
  </Center>
)

const VersionCheckProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth()
  const dispatch = useDispatch()

  // Always poll — even when logged out
  useVersionCheck(true)

  const { hasNewVersion, dismissed, dismissCount } = useSelector((state: RootState) => state.version)

  const handleDismiss = () => {
    dispatch(dismissUpdate())
  }

  // Prefetch all section chunks once the user is authenticated.
  // This ensures chunks are in the browser/SW cache before a new deploy
  // removes the old hashed files from the server.
  useEffect(() => {
    if (user) {
      prefetchAllSections()
    }
  }, [user])

  // Auto-refresh if not logged in and a new version is available
  useEffect(() => {
    if (!user && hasNewVersion) {
      refreshApp()
    }
  }, [user, hasNewVersion])

  // PWA recovery: when the app resumes from background (common on mobile PWAs
  // that stay alive for hours), validate the session. If the token has expired
  // the sections will be rendering blank because their GraphQL calls fail
  // silently. A hard reload clears stale SW caches and re-bootstraps cleanly.
  useEffect(() => {
    let lastVisibleAt = Date.now()

    const handleVisibilityChange = async () => {
      if (document.visibilityState === "visible") {
        const elapsed = Date.now() - lastVisibleAt
        // Only check if the app was hidden for more than 2 minutes
        if (elapsed > 2 * 60 * 1000 && user) {
          try {
            const token = localStorage.getItem("bearer_token")
            if (!token) {
              // Token was cleared (session expired) — reload to re-auth
              console.warn("[PWA Recovery] No bearer token found after resume, reloading")
              refreshApp()
              return
            }

            // Validate the token is still accepted by the auth server
            const { authClient } = await import("./auth/auth")
            const sessionData = await authClient.getSession()
            if (!sessionData?.data?.session) {
              console.warn("[PWA Recovery] Session invalid after resume, reloading")
              localStorage.removeItem("bearer_token")
              refreshApp()
              return
            }

            // Session is valid — update the stored token if refreshed
            const freshToken = sessionData.data.session.token
            if (freshToken && freshToken !== token) {
              localStorage.setItem("bearer_token", freshToken)
            }
          } catch (err) {
            console.warn("[PWA Recovery] Session check failed after resume, reloading", err)
            refreshApp()
          }
        }
        lastVisibleAt = Date.now()
      } else {
        lastVisibleAt = Date.now()
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange)
  }, [user])

  // Only show the banner when logged in
  const shouldShowToast = !!user && hasNewVersion && !dismissed

  return (
    <>
      {shouldShowToast && (
        <VersionToast
          hasNewVersion={true}
          onDismiss={handleDismiss}
          previouslyDismissed={dismissCount > 0}
        />
      )}
      {children}
    </>
  )
}

export function Providers({ children }: { children: React.ReactNode }) {
  const system = createStylingSystem("#root")

  return (
    <ReduxProvider store={store}>
      {/* Sole owner of the Colour Mode. Light until the user picks Dark or System
          in User Settings; SectionLoader mirrors the result into each section. */}
      <Provider system={system} defaultTheme="light">
        <AuthApolloProvider uri={graphqlUri}>
          <AuthProvider loader={<FullPageLoader />}>
            <VersionCheckProvider>{children}</VersionCheckProvider>
          </AuthProvider>
        </AuthApolloProvider>
      </Provider>
    </ReduxProvider>
  )
}
